import { emitCompletionAlert } from "@/entities/notification/completion-alerts"
import { BROWSER_COMPLETION_NOTIFICATIONS_KEY } from "@/shared/lib/storage/device-preference"
import {
  ASSISTANT_COMPLETION_NOTIFICATIONS_KEY,
  onSessionRemoved,
} from "@/shared/lib/token/token"

export const ASSISTANT_COMPLETION_CHANGE = "assistant-completion-change"

// A request may spend time uploading before a run becomes visible. Once accepted, the server
// gives generation an absolute 30-minute deadline; allow a little time for persistence/retries.
export const ASSISTANT_ACCEPTANCE_MS = 5 * 60 * 1000
const RUN_MS = 35 * 60 * 1000

export type WatchedAssistantRequest = {
  conversationId: string
  requestId: string
  startedAt: number
  seenActive: boolean
}

type UnreadCompletion = {
  conversationId: string
  requestId: string
}

type CompletionState = {
  watching: WatchedAssistantRequest[]
  unread: UnreadCompletion[]
  browserNotificationsEnabled: boolean
}

const emptyState = (): CompletionState => ({
  watching: [],
  unread: [],
  browserNotificationsEnabled: false,
})

let fallback = emptyState()
let storageDirty = false

const readState = (): CompletionState => {
  if (storageDirty) return fallback
  try {
    const raw: unknown = JSON.parse(
      sessionStorage.getItem(ASSISTANT_COMPLETION_NOTIFICATIONS_KEY) ?? "null"
    )
    const state = raw as Partial<CompletionState> | null
    const watching = Array.isArray(state?.watching)
      ? state.watching.filter(
          (entry): entry is WatchedAssistantRequest =>
            typeof entry?.conversationId === "string" &&
            typeof entry?.requestId === "string" &&
            typeof entry?.startedAt === "number" &&
            typeof entry?.seenActive === "boolean"
        )
      : []
    const unread = Array.isArray(state?.unread)
      ? state.unread.filter(
          (entry): entry is UnreadCompletion =>
            typeof entry?.conversationId === "string" &&
            typeof entry?.requestId === "string"
        )
      : []
    fallback = {
      watching,
      unread,
      browserNotificationsEnabled: state?.browserNotificationsEnabled === true,
    }
    return fallback
  } catch {
    storageDirty = true
    return fallback
  }
}

const publish = (state: CompletionState) => {
  fallback = state
  try {
    sessionStorage.setItem(
      ASSISTANT_COMPLETION_NOTIFICATIONS_KEY,
      JSON.stringify(state)
    )
    storageDirty = false
  } catch {
    storageDirty = true
    // A blocked/full sessionStorage still allows this tab to watch in memory.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(ASSISTANT_COMPLETION_CHANGE))
  }
}

/** Keep only request IDs and conversation IDs; never persist prompts or document names. */
export const watchAssistantRequest = (
  conversationId: string,
  requestId: string
) => {
  const state = readState()
  if (state.watching.some((entry) => entry.requestId === requestId)) return
  publish({
    ...state,
    watching: [
      ...state.watching,
      { conversationId, requestId, startedAt: Date.now(), seenActive: false },
    ],
  })
}

export const watchedAssistantRequests = () => {
  return readState().watching
}

export const assistantRunWatchExpired = (entry: WatchedAssistantRequest) =>
  entry.seenActive && Date.now() - entry.startedAt >= RUN_MS

export const markAssistantRequestActive = (requestId: string) => {
  const state = readState()
  if (
    !state.watching.some(
      (entry) => entry.requestId === requestId && !entry.seenActive
    )
  )
    return
  publish({
    ...state,
    watching: state.watching.map((entry) =>
      entry.requestId === requestId
        ? { ...entry, seenActive: true, startedAt: Date.now() }
        : entry
    ),
  })
}

/** Returns false if cancellation or another observer already retired this exact request. */
export const finishWatchedAssistantRequest = (
  conversationId: string,
  requestId: string,
  unread: boolean
) => {
  const state = readState()
  if (
    !state.watching.some(
      (entry) =>
        entry.requestId === requestId && entry.conversationId === conversationId
    )
  ) {
    return false
  }
  publish({
    ...state,
    watching: state.watching.filter((entry) => entry.requestId !== requestId),
    unread: unread
      ? [...state.unread, { conversationId, requestId }]
      : state.unread,
  })
  emitCompletionAlert({ kind: "assistant", resourceId: conversationId, runId: requestId })
  return true
}

/** An explicit Stop or rejected send must not announce a successful evaluation later. */
export const forgetWatchedAssistantRequest = (requestId: string) => {
  const state = readState()
  if (!state.watching.some((entry) => entry.requestId === requestId)) return
  publish({
    ...state,
    watching: state.watching.filter((entry) => entry.requestId !== requestId),
  })
}

export const unreadAssistantCompletions = () => readState().unread

export const acknowledgeAssistantConversation = (conversationId: string) => {
  const state = readState()
  if (!state.unread.some((entry) => entry.conversationId === conversationId))
    return
  publish({
    ...state,
    unread: state.unread.filter(
      (entry) => entry.conversationId !== conversationId
    ),
  })
}

/** Delete is final: no pending or unread alert may survive its conversation. */
export const clearAssistantConversationCompletionState = (
  conversationId: string
) => {
  const state = readState()
  if (
    !state.watching.some((entry) => entry.conversationId === conversationId) &&
    !state.unread.some((entry) => entry.conversationId === conversationId)
  )
    return
  publish({
    ...state,
    watching: state.watching.filter(
      (entry) => entry.conversationId !== conversationId
    ),
    unread: state.unread.filter(
      (entry) => entry.conversationId !== conversationId
    ),
  })
}

// Retain a failed write in this tab, and retry when storage becomes available.
let pendingBrowserPreference: boolean | undefined
let lastBrowserPreference: boolean | undefined

const persistBrowserPreference = (enabled: boolean) => {
  lastBrowserPreference = enabled
  pendingBrowserPreference = enabled
  try {
    window.localStorage.setItem(
      BROWSER_COMPLETION_NOTIFICATIONS_KEY,
      String(enabled)
    )
    pendingBrowserPreference = undefined
  } catch {
    // A blocked/full localStorage still allows the setting to work in this tab.
  }
}

export const browserAssistantNotificationsEnabled = () => {
  if (pendingBrowserPreference !== undefined) {
    const enabled = pendingBrowserPreference
    persistBrowserPreference(enabled)
    return enabled
  }
  try {
    const stored = window.localStorage.getItem(
      BROWSER_COMPLETION_NOTIFICATIONS_KEY
    )
    if (stored !== null) {
      lastBrowserPreference = stored === "true"
      return lastBrowserPreference
    }
    lastBrowserPreference = undefined
  } catch {
    if (lastBrowserPreference !== undefined) return lastBrowserPreference
  }
  // Migrate an already-enabled setting from the previous session-only format.
  const enabled = readState().browserNotificationsEnabled
  if (enabled) persistBrowserPreference(true)
  return enabled
}

export const setBrowserAssistantNotificationsEnabled = (enabled: boolean) => {
  persistBrowserPreference(enabled)
  const state = readState()
  // Keep the legacy field for tabs still using the previous bundle.
  publish({ ...state, browserNotificationsEnabled: enabled })
}

// A later choice in another tab takes precedence over this tab's failed write,
// even while the button is unmounted (for example, on a CAT review page).
const syncBrowserPreference = (event: StorageEvent) => {
  if (event.key !== BROWSER_COMPLETION_NOTIFICATIONS_KEY && event.key !== null)
    return
  if (event.storageArea !== null && event.storageArea !== window.localStorage)
    return
  pendingBrowserPreference = undefined
  lastBrowserPreference = event.newValue === "true"
  publish({ ...readState(), browserNotificationsEnabled: lastBrowserPreference })
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", syncBrowserPreference)
}

export const clearAssistantCompletionNotifications = () => {
  fallback = emptyState()
  try {
    sessionStorage.removeItem(ASSISTANT_COMPLETION_NOTIFICATIONS_KEY)
    storageDirty = false
  } catch {
    storageDirty = true
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(ASSISTANT_COMPLETION_CHANGE))
  }
}

onSessionRemoved(clearAssistantCompletionNotifications)
